/**
 * Validador de calidad del editor de blog (Portal).
 * Bloqueantes (a–e): al publicar piden confirmación «Publicar igualmente».
 * Avisos (f–i): se muestran y no impiden publicar.
 */

export const META_TITLE_MAX = 60;
export const META_DESCRIPTION_MAX = 160;
export const BODY_MIN_WORDS = 600;
export const BLOG_BLOB_ORIGIN = 'https://aipecesprost.blob.core.windows.net';
export const BLOG_QUALITY_PUBLISH_LOG = '[blog-quality] Publicar igualmente';

export type BlogQualityRuleId =
    | 'markers'
    | 'capture_counts'
    | 'empty_sections'
    | 'jsonld'
    | 'faq'
    | 'internal_links'
    | 'cdn_images'
    | 'seo_length'
    | 'word_count';

export type BlogQualitySeverity = 'blocking' | 'warning';

export interface BlogQualityIssue {
    rule: BlogQualityRuleId;
    severity: BlogQualitySeverity;
    message: string;
    details: string[];
}

export type BlogSlugCheck =
    | { state: 'pending' }
    | { state: 'error' }
    | { state: 'ready'; statusBySlug: Readonly<Record<string, string>> };

export interface BlogQualityInput {
    title: string;
    metaTitle: string;
    metaDescription: string;
    excerpt: string;
    bodyHtml: string;
    /** Slug del artículo en edición: un enlace a sí mismo no se avisa. */
    currentSlug?: string;
    /**
     * Resultado de admin_blog_slugs_status.
     * pending = todavía no hay respuesta; error = la RPC falló;
     * ready.statusBySlug = slugs que existen (clave en minúsculas) → status.
     */
    slugCheck?: BlogSlugCheck;
}

export interface BlogQualityReport {
    issues: BlogQualityIssue[];
    blocking: BlogQualityIssue[];
    warnings: BlogQualityIssue[];
}

const MARKERS: Array<{ label: string; re: RegExp }> = [
    { label: '[TIENDA-', re: /\[TIENDA-/ },
    { label: 'TODO', re: /\bTODO\b/ },
    { label: 'VB ', re: /\bVB / },
    { label: 'CHAT-1/2/3', re: /CHAT-(?:1|2|3)(?!\d)/ },
    { label: 'CURSOR-', re: /CURSOR-/ },
    { label: 'fail-closed', re: /fail-closed/i },
    { label: 'lorem', re: /\blorem\b/i },
    { label: 'comentario HTML', re: /<!--[\s\S]*?-->/ },
];

const FIELDS: Array<{ key: string; pick: (input: BlogQualityInput) => string }> = [
    { key: 'title', pick: (input) => input.title },
    { key: 'meta_title', pick: (input) => input.metaTitle },
    { key: 'meta_description', pick: (input) => input.metaDescription },
    { key: 'excerpt', pick: (input) => input.excerpt },
    { key: 'body_html', pick: (input) => input.bodyHtml },
];

function unique(items: string[]): string[] {
    return [...new Set(items)];
}

function issue(
    rule: BlogQualityRuleId,
    severity: BlogQualitySeverity,
    message: string,
    details: string[],
): BlogQualityIssue | null {
    if (details.length === 0 && rule !== 'word_count') return null;
    return { rule, severity, message, details };
}

function safeCodePoint(code: number): string {
    if (!Number.isFinite(code) || code < 1 || code > 0x10ffff) return '';
    try {
        return String.fromCodePoint(code);
    } catch {
        return '';
    }
}

function decodeBasicEntities(text: string): string {
    return text
        .replace(/&#(\d+);/g, (_, n) => safeCodePoint(Number(n)))
        .replace(/&#x([0-9a-f]+);/gi, (_, h) => safeCodePoint(parseInt(h, 16)))
        .replace(/&nbsp;/gi, ' ')
        .replace(/&iquest;/gi, '¿')
        .replace(/&iexcl;/gi, '¡')
        .replace(/&aacute;/gi, 'á')
        .replace(/&eacute;/gi, 'é')
        .replace(/&iacute;/gi, 'í')
        .replace(/&oacute;/gi, 'ó')
        .replace(/&uacute;/gi, 'ú')
        .replace(/&ntilde;/gi, 'ñ')
        .replace(/&uuml;/gi, 'ü')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&amp;/gi, '&');
}

/** Texto visible: sin etiquetas, espacios colapsados. */
export function normalizeFaqText(raw: string): string {
    const withoutTags = String(raw || '').replace(/<[^>]+>/g, ' ');
    return decodeBasicEntities(withoutTags).replace(/\s+/g, ' ').trim();
}

function stripScriptsStylesComments(html: string): string {
    return String(html || '')
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '');
}

function stripNav(html: string): string {
    return html.replace(/<nav\b[^>]*>[\s\S]*?<\/nav>/gi, ' ');
}

function plainProse(html: string): string {
    return normalizeFaqText(stripScriptsStylesComments(html));
}

function clip(text: string, max = 140): string {
    const trimmed = text.replace(/\s+/g, ' ').trim();
    if (trimmed.length <= max) return trimmed;
    return `${trimmed.slice(0, max - 1)}…`;
}

function markerDetails(input: BlogQualityInput): string[] {
    const details: string[] = [];
    for (const field of FIELDS) {
        const text = field.pick(input) || '';
        const hits = MARKERS.filter((marker) => marker.re.test(text)).map((marker) => marker.label);
        if (hits.length) details.push(`${field.key}: ${hits.join(', ')}`);
    }
    return details;
}

function isCaptureParen(raw: string): boolean {
    if (/^\d{1,3}(?:\.\d{3})+$/.test(raw)) return true;
    if (!/^\d{2,}$/.test(raw)) return false;
    const year = Number(raw);
    if (raw.length === 4 && year >= 1900 && year <= 2099) return false;
    return true;
}

function captureDetailsForText(label: string, text: string): string[] {
    const prose = plainProse(text);
    const details: string[] = [];
    const nameRe = /(^|[^\p{L}])(\p{L}{2,}(?:\s+\p{L}{2,}){0,3})\s+\(([\d.]+)\)/gu;
    for (const match of prose.matchAll(nameRe)) {
        if (!isCaptureParen(match[3])) continue;
        details.push(`${label}: ${match[2]} (${match[3]})`);
    }
    const countRe = /\b(\d{1,3}(?:\.\d{3})+|\d+)(?!\s*%)\s+(capturas?|registros?)\b/gi;
    for (const match of prose.matchAll(countRe)) {
        details.push(`${label}: ${match[1]} ${match[2]}`);
    }
    return details;
}

function captureDetails(input: BlogQualityInput): string[] {
    return unique(FIELDS.flatMap((field) => captureDetailsForText(field.key, field.pick(input) || '')));
}

function hasSectionContent(fragment: string): boolean {
    if (normalizeFaqText(fragment)) return true;
    return /<(img|picture|video|iframe|svg|table|figure|canvas|object)\b/i.test(fragment);
}

/** Hasta el siguiente h2/h3 del mismo nivel o superior. Un h3 con texto no vacía el h2 padre. */
function emptySectionDetails(html: string): string[] {
    const cleaned = stripScriptsStylesComments(html);
    const re = /<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi;
    const headings: Array<{ level: number; text: string; end: number; start: number }> = [];
    for (const match of cleaned.matchAll(re)) {
        const start = match.index ?? 0;
        headings.push({
            level: Number(match[1]),
            text: normalizeFaqText(match[2]),
            start,
            end: start + match[0].length,
        });
    }
    const details: string[] = [];
    for (let i = 0; i < headings.length; i += 1) {
        const heading = headings[i];
        if (heading.level !== 2 && heading.level !== 3) continue;
        let end = cleaned.length;
        let closedBySibling = false;
        for (let j = i + 1; j < headings.length; j += 1) {
            if (headings[j].level <= heading.level) {
                end = headings[j].start;
                closedBySibling = true;
                break;
            }
        }
        const between = cleaned
            .slice(heading.end, end)
            .replace(/<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>/gi, '');
        if (hasSectionContent(between)) continue;
        const label = heading.text || `(h${heading.level} sin texto)`;
        details.push(
            closedBySibling
                ? `«${label}» (h${heading.level}) no tiene contenido antes del siguiente encabezado`
                : `«${label}» (h${heading.level}) no tiene contenido antes del final del cuerpo`,
        );
    }
    return details;
}

interface LdJsonScript {
    index: number;
    parsed?: unknown;
    error?: string;
}

function ldJsonScripts(html: string): LdJsonScript[] {
    const re = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
    const out: LdJsonScript[] = [];
    let index = 0;
    for (const match of html.matchAll(re)) {
        const typeMatch = /\btype\s*=\s*(["'])([^"']+)\1/i.exec(match[1]);
        if (!typeMatch || typeMatch[2].trim().toLowerCase() !== 'application/ld+json') continue;
        index += 1;
        try {
            out.push({ index, parsed: JSON.parse(match[2].trim()) });
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            out.push({ index, error: message });
        }
    }
    return out;
}

function isFaqPage(value: unknown): boolean {
    const types = Array.isArray(value) ? value : [value];
    return types.some((type) => String(type) === 'FAQPage');
}

function collectFaqPages(node: unknown, out: Record<string, unknown>[]): void {
    if (Array.isArray(node)) {
        node.forEach((item) => collectFaqPages(item, out));
        return;
    }
    if (!node || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;
    if (isFaqPage(obj['@type'])) out.push(obj);
    if (obj['@graph'] != null) collectFaqPages(obj['@graph'], out);
}

function answerText(value: unknown): string {
    const answers = Array.isArray(value) ? value : value ? [value] : [];
    return answers
        .map((answer) => {
            if (!answer || typeof answer !== 'object') return '';
            return String((answer as Record<string, unknown>).text ?? '');
        })
        .filter(Boolean)
        .join(' ');
}

function faqPairs(page: Record<string, unknown>): Array<{ q: string; a: string }> {
    const raw = page.mainEntity;
    const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
    return list.map((item) => {
        const question = (item ?? {}) as Record<string, unknown>;
        return {
            q: String(question.name ?? ''),
            a: answerText(question.acceptedAnswer),
        };
    });
}

interface VisibleH3 {
    question: string;
    answer: string;
}

function visibleH3Answers(html: string): VisibleH3[] {
    const cleaned = stripScriptsStylesComments(html);
    const h3re = /<h3\b[^>]*>([\s\S]*?)<\/h3>/gi;
    const found: Array<{ question: string; end: number }> = [];
    for (const match of cleaned.matchAll(h3re)) {
        const start = match.index ?? 0;
        found.push({
            question: normalizeFaqText(match[1]),
            end: start + match[0].length,
        });
    }
    return found.map((heading) => {
        const nextRe = /<h[1-6]\b/gi;
        nextRe.lastIndex = heading.end;
        const next = nextRe.exec(cleaned);
        const slice = cleaned.slice(heading.end, next ? next.index : cleaned.length);
        return { question: heading.question, answer: normalizeFaqText(slice) };
    });
}

function faqMismatchDetails(html: string, scripts: LdJsonScript[]): string[] {
    const pages: Record<string, unknown>[] = [];
    for (const script of scripts) {
        if (script.parsed != null) collectFaqPages(script.parsed, pages);
    }
    if (pages.length === 0) return [];

    const visible = visibleH3Answers(html);
    const byQuestion = new Map<string, string>();
    for (const item of visible) {
        if (!byQuestion.has(item.question)) byQuestion.set(item.question, item.answer);
    }

    const details: string[] = [];
    for (const page of pages) {
        const pairs = faqPairs(page);
        if (pairs.length === 0) {
            details.push('FAQPage sin preguntas en mainEntity');
            continue;
        }
        for (const pair of pairs) {
            const question = normalizeFaqText(pair.q);
            if (!question) {
                details.push('Pregunta vacía en el JSON-LD');
                continue;
            }
            if (!byQuestion.has(question)) {
                details.push(`Pregunta sin <h3> visible: «${clip(question)}»`);
                continue;
            }
            const visibleAnswer = byQuestion.get(question) ?? '';
            const ldAnswer = normalizeFaqText(pair.a);
            if (visibleAnswer !== ldAnswer) {
                details.push(
                    `Respuesta distinta para «${clip(question, 80)}»: visible «${clip(visibleAnswer)}» / JSON-LD «${clip(ldAnswer)}»`,
                );
            }
        }
    }
    return unique(details);
}

export const BLOG_INLINE_FAQ_WARNING =
    'Este artículo tiene un FAQPage escrito a mano; ya no se usa: Google recibe las preguntas visibles. Puedes borrarlo';

/** Avisa de cualquier FAQPage inline: el render publica los h3 visibles, no este JSON-LD. */
function inlineFaqDetails(html: string, scripts: LdJsonScript[]): string[] {
    const hasFaqPage = scripts.some((script) => {
        if (script.parsed == null) return false;
        const pages: Record<string, unknown>[] = [];
        collectFaqPages(script.parsed, pages);
        return pages.length > 0;
    });
    if (!hasFaqPage) return [];
    const mismatches = faqMismatchDetails(html, scripts);
    return mismatches.length ? mismatches : ['El FAQPage coincide con las preguntas visibles, pero se ignora.'];
}

const BLOG_HOSTS = new Set(['aipeces.com', 'www.aipeces.com']);

export function blogSlugFromHref(href: string): string | null {
    const trimmed = String(href || '').trim();
    if (!trimmed || /^(mailto:|tel:|javascript:)/i.test(trimmed)) return null;
    let path = trimmed;
    if (/^https?:\/\//i.test(trimmed)) {
        let url: URL;
        try {
            url = new URL(trimmed);
        } catch {
            return null;
        }
        if (!BLOG_HOSTS.has(url.hostname.toLowerCase())) return null;
        path = `${url.pathname}${url.search}${url.hash}`;
    } else if (!trimmed.startsWith('/')) {
        return null;
    }
    const pathOnly = path.split('#')[0].split('?')[0];
    const match = pathOnly.match(/^\/blog\/([^/]+)\/?$/i);
    if (!match) return null;
    let slug = match[1];
    try {
        slug = decodeURIComponent(slug);
    } catch {
        /* slug tal cual */
    }
    slug = slug.trim().toLowerCase();
    return slug || null;
}

export function extractInternalBlogSlugs(html: string): string[] {
    const slugs = new Set<string>();
    const re = /<a\b[^>]*\bhref\s*=\s*(["'])([^"']*)\1/gi;
    for (const match of String(html || '').matchAll(re)) {
        const slug = blogSlugFromHref(match[2]);
        if (slug) slugs.add(slug);
    }
    return [...slugs];
}

function linkDetails(html: string, currentSlug: string, slugCheck: BlogSlugCheck | undefined): string[] {
    const slugs = extractInternalBlogSlugs(html).filter((slug) => slug !== currentSlug.trim().toLowerCase());
    if (slugs.length === 0) return [];
    if (!slugCheck || slugCheck.state === 'pending') return [];
    if (slugCheck.state === 'error') {
        return ['No se pudo comprobar los enlaces internos (admin_blog_slugs_status).'];
    }
    const details: string[] = [];
    for (const slug of slugs) {
        const status = slugCheck.statusBySlug[slug];
        if (!status) details.push(`/blog/${slug} no existe`);
        else if (status.toLowerCase() === 'disabled') details.push(`/blog/${slug} está disabled`);
    }
    return details;
}

export function azureFdImageSrcs(html: string): string[] {
    const srcs: string[] = [];
    const re = /<img\b[^>]*>/gi;
    for (const match of String(html || '').matchAll(re)) {
        const srcMatch = match[0].match(/\bsrc\s*=\s*(["'])([^"']*)\1/i);
        if (!srcMatch) continue;
        if (azureFdToBlob(srcMatch[2])) srcs.push(srcMatch[2]);
    }
    return unique(srcs);
}

function azureFdToBlob(src: string): string | null {
    const trimmed = src.trim().replace(/&amp;/g, '&');
    if (!trimmed) return null;
    let url: URL;
    try {
        url = trimmed.startsWith('//') ? new URL(`https:${trimmed}`) : new URL(trimmed);
    } catch {
        return null;
    }
    const host = url.hostname.toLowerCase();
    if (host !== 'azurefd.net' && !host.endsWith('.azurefd.net')) return null;
    return `${BLOG_BLOB_ORIGIN}${url.pathname}${url.search}`;
}

/** Cambia el host *.azurefd.net de los <img> al blob, conservando path y query. */
export function rewriteAzureFdImagesToBlob(html: string): { html: string; rewritten: number } {
    let rewritten = 0;
    const nextHtml = String(html || '').replace(/<img\b[^>]*>/gi, (tag) => {
        const srcMatch = tag.match(/\bsrc\s*=\s*(["'])([^"']*)\1/i);
        if (!srcMatch) return tag;
        const next = azureFdToBlob(srcMatch[2]);
        if (!next || next === srcMatch[2]) return tag;
        rewritten += 1;
        const quote = srcMatch[1];
        return tag.replace(srcMatch[0], `src=${quote}${next}${quote}`);
    });
    return { html: nextHtml, rewritten };
}

function seoLengthDetails(metaTitle: string, metaDescription: string): string[] {
    const details: string[] = [];
    if (metaTitle.length > META_TITLE_MAX) {
        details.push(`meta_title tiene ${metaTitle.length} caracteres (máximo ${META_TITLE_MAX})`);
    }
    if (metaDescription.length > META_DESCRIPTION_MAX) {
        details.push(`meta_description tiene ${metaDescription.length} caracteres (máximo ${META_DESCRIPTION_MAX})`);
    }
    return details;
}

export function countBlogBodyWords(html: string): number {
    const cleaned = stripNav(stripScriptsStylesComments(html));
    const text = normalizeFaqText(cleaned);
    const words = text.match(/\p{L}+(?:['’\-]\p{L}+)?/gu);
    return words ? words.length : 0;
}

export function analyzeBlogQuality(input: BlogQualityInput): BlogQualityReport {
    const currentSlug = input.currentSlug ?? '';
    const slugCheck = input.slugCheck;
    const scripts = ldJsonScripts(input.bodyHtml || '');
    const wordCount = countBlogBodyWords(input.bodyHtml || '');

    const built: Array<BlogQualityIssue | null> = [
        issue(
            'markers',
            'blocking',
            'Marcadores o notas internas',
            markerDetails(input),
        ),
        issue(
            'capture_counts',
            'blocking',
            'Cifras exactas de capturas (usa porcentaje o lenguaje cualitativo)',
            captureDetails(input),
        ),
        issue(
            'empty_sections',
            'blocking',
            'Secciones vacías',
            emptySectionDetails(input.bodyHtml || ''),
        ),
        issue(
            'jsonld',
            'blocking',
            'JSON-LD inválido',
            scripts
                .filter((script) => script.error)
                .map((script) => `script JSON-LD ${script.index}: ${script.error}`),
        ),
        issue(
            'faq',
            'warning',
            BLOG_INLINE_FAQ_WARNING,
            inlineFaqDetails(input.bodyHtml || '', scripts),
        ),
        issue(
            'internal_links',
            'warning',
            'Enlaces internos a artículos que no existen o están retirados',
            linkDetails(input.bodyHtml || '', currentSlug, slugCheck),
        ),
        issue(
            'cdn_images',
            'warning',
            'Imágenes del cuerpo en *.azurefd.net (el saneador las elimina al guardar)',
            azureFdImageSrcs(input.bodyHtml || ''),
        ),
        issue(
            'seo_length',
            'warning',
            'SEO demasiado largo',
            seoLengthDetails(input.metaTitle || '', input.metaDescription || ''),
        ),
        wordCount < BODY_MIN_WORDS
            ? {
                rule: 'word_count',
                severity: 'warning',
                message: `Cuerpo por debajo de ${BODY_MIN_WORDS} palabras (ahora ${wordCount}, sin scripts ni navegación)`,
                details: [],
            }
            : null,
    ];

    const issues = built.filter((item): item is BlogQualityIssue => item != null && (item.details.length > 0 || item.rule === 'word_count'));
    return {
        issues,
        blocking: issues.filter((item) => item.severity === 'blocking'),
        warnings: issues.filter((item) => item.severity === 'warning'),
    };
}

export function shouldAskPublishConfirm(
    status: string,
    blockingCount: number,
    alreadyConfirmed: boolean,
): boolean {
    return status === 'enabled' && blockingCount > 0 && !alreadyConfirmed;
}

export function blogQualityPublishRecord(slug: string, blocking: BlogQualityIssue[]) {
    return {
        slug,
        blocking: blocking.map((item) => ({
            rule: item.rule,
            message: item.message,
            details: item.details,
        })),
    };
}
